import { isUndefined } from 'lodash-es';

import { handleFormatter } from './utils/handleFormatter.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { reduceExploreSelect } from '../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import { SelectionArea } from '../../../core/components/selection-area/SelectionArea.js';
import { RelativeAbsolutePosition } from '../../../core/types/pointer-events.js';
import { isBandScale } from '../../../core/utils/detect-scale.js';
import { useAxesFormatter } from '../../hooks/useAxesFormatter.js';
import { useVisibleAxesConfig } from '../../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../../hooks/useVisibleAxesScales.js';
import { useXYChartOverlayRef } from '../../hooks/useXYChartOverlayRef.js';
import { useSelectState, useSetState } from '../../store/selectors.js';
import {
  AnyXYChartScale,
  XYChartAxisInternal,
} from '../../types/xy-chart-internals.js';

export const XYChartSelectionArea = () => {
  const setState = useSetState();
  const chartRef = useXYChartOverlayRef();
  const axesConfig = useVisibleAxesConfig();
  const axesIdToFormatter = useAxesFormatter();
  const axesScales = useVisibleAxesScales();
  const selectState = useSelectState();
  const { width } = useGridPlotSize();

  const chartBounds = chartRef.current?.getBoundingClientRect();
  if (isUndefined(selectState) || isUndefined(chartBounds)) {
    return null;
  }

  const isBottomLabelRotated =
    axesConfig['bottom']?.tickLabelLayout === 'vertical';
  const isTopLabelRotated = axesConfig['top']?.tickLabelLayout === 'vertical';

  const { start, end, finished, accessible, variant } = selectState;

  const handleSelectionChange = ({
    selection,
    accessible,
  }: {
    selection: [RelativeAbsolutePosition, RelativeAbsolutePosition];
    accessible: boolean;
  }) => {
    setState(
      reduceExploreSelect({
        start: selection[0],
        end: selection[1],
        finished: true,
        accessible,
        variant,
      }),
    );
  };

  const formatterHandler = (
    config?: XYChartAxisInternal,
    scale?: AnyXYChartScale,
  ) => {
    if (config && scale && !isBandScale(scale)) {
      return (value: number) =>
        handleFormatter(config, axesIdToFormatter, scale, value, width);
    }
  };

  return (
    <SelectionArea
      selection={[start, end]}
      bounds={chartBounds}
      variant={variant}
      focusable={accessible}
      interactable={finished && !accessible}
      bottomFormatter={formatterHandler(axesConfig.bottom, axesScales.bottom)}
      topFormatter={formatterHandler(axesConfig.top, axesScales.top)}
      onKeyDown={() => void 0}
      onSelectionChange={handleSelectionChange}
      isBottomLabelRotated={isBottomLabelRotated}
      isTopLabelRotated={isTopLabelRotated}
    />
  );
};
